import _ from 'lodash'
import React, { useState, FC } from 'react'
import { Modal, message, Form, Input, Select } from 'antd'
import {
    CloseOutlined,
    PaperClipOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { DocumentNode } from '@apollo/client'
import UploadMessageAttachment from '@OpsQuality/components/MessagesUI/components/UploadMessageAttachment'
import { adminJobRequestQuery_jobRequest_subjectTypes as SubjectType } from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'

import styles from './styles.css'
import { MutationOperations, formatFileSize } from './logic'

const ATTACHMENTS_TOTAL_SIZE_LIMIT = 26214400
const MAX_FILE_NAME_DISPLAY_LENGTH = 35
const OTHER_OPTION = 'Other'
const OTHER = 'OTHER'
const MODAL_TITLES = {
    scheduling: 'Send a message',
    quality: 'Send a Message about this job request',
}

type MessageModalPropsType = {
    openMessageModal: boolean
    jobRequestId: number
    subjectTypes: SubjectType[] | undefined
    onClose: () => void
    successContent: { title: string }
    refetchQuery: DocumentNode
    usedInScheduling?: boolean
    itemPartNames?: string[]
    partName?: string
}

type AttachmentType = {
    id: number
    name: string
    size: number
}

const MessagesModal: FC<MessageModalPropsType> = props => {
    const {
        openMessageModal,
        onClose,
        successContent,
        refetchQuery,
        jobRequestId,
        usedInScheduling,
        itemPartNames,
        subjectTypes,
    } = props
    const [attachments, setAttachments] = useState<AttachmentType[]>([])
    const [isSendButtonDisabled, setSendButtonDisabled] = useState(false)
    const [previousFileCount, setPreviousFileCount] = useState(0)
    const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>(
        [],
    )
    const [showAttachmentSizeMessage, setShowAttachmentSizeMessage] =
        useState(false)
    const [loading, setLoading] = useState<boolean>(false)

    const [form] = Form.useForm()
    const { isFieldsTouched, getFieldsValue, resetFields } = form
    const { createJobRequestComment } = MutationOperations()

    const resetStates = () => {
        setAttachments([])
        setPreviousFileCount(0)
        setRemovedAttachmentIds([])
    }

    const handleSubmit = async () => {
        setLoading(true)
        if (!isFieldsTouched()) {
            return
        }
        const values = getFieldsValue(true)
        const attachmentIds = attachments.map(att => att.id)

        try {
            await createJobRequestComment(
                `${jobRequestId}`,
                values.message.replace(/\n/g, '<br/>').trim(),
                values.itemPartName || OTHER_OPTION,
                usedInScheduling ? OTHER : values.subject || OTHER_OPTION,
                attachmentIds,
                refetchQuery,
            )
            message.success(successContent.title, 3)
            setLoading(false)
            resetStates()
            resetFields()
            onClose()
        } catch (err) {
            message.error('Unable to send the message', 3)
            setLoading(false)
            onClose()
        }
    }

    const checkAttachmentsSize = () => {
        let totalAttachedFileSize = 0

        attachments.map(attachment => {
            totalAttachedFileSize += attachment.size
        })

        if (totalAttachedFileSize > ATTACHMENTS_TOTAL_SIZE_LIMIT) {
            setSendButtonDisabled(true)
            setShowAttachmentSizeMessage(true)
        } else {
            setSendButtonDisabled(false)
            setShowAttachmentSizeMessage(false)
        }
    }

    const onUploadComplete = (data: any) => {
        setSendButtonDisabled(false)
        const newAttachments = data
            .filter(
                (file: any) =>
                    !removedAttachmentIds.includes(file.erpAttachmentId) &&
                    file.status === 'done',
            )

            .map((file: any) => ({
                name: file.name,
                id: file.erpAttachmentId,
                size: file.size,
            }))

        setAttachments(newAttachments)
        setPreviousFileCount(attachments.length)
        checkAttachmentsSize()
    }

    const removeAttachment = (id: number) => {
        const filteredAttachments = attachments.filter(
            attachment => attachment.id !== id,
        )

        removedAttachmentIds.push(id)
        setAttachments(filteredAttachments)
        setPreviousFileCount(previousFileCount - 1)
        setRemovedAttachmentIds(removedAttachmentIds)
        checkAttachmentsSize()
    }

    return (
        <Modal
            title={
                usedInScheduling
                    ? MODAL_TITLES.scheduling
                    : MODAL_TITLES.quality
            }
            visible={openMessageModal}
            onCancel={() => {
                resetFields()
                onClose()
            }}
            footer={[
                <Button
                    key="cancel"
                    onClick={() => {
                        resetFields()
                        onClose()
                    }}
                    placement="messagesModal"
                    name="cancel"
                    disabled={loading}
                    className={styles.messageModalButtonStyle}
                    data-test-id="cancel-message-modal-button"
                    data-test-target="cancel-message-button"
                >
                    Cancel
                </Button>,
                <Button
                    type="primary"
                    form="addMessageForm"
                    key="addMessage"
                    htmlType="submit"
                    placement="messagesModal"
                    name="send"
                    loading={loading}
                    className={styles.messageModalButtonStyle}
                    data-test-target="Send-message-button"
                    disabled={isSendButtonDisabled}
                >
                    Send
                </Button>,
            ]}
            width="520px"
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={() => {
                    handleSubmit()
                }}
                id="addMessageForm"
            >
                {!usedInScheduling && (
                    <>
                        <Text
                            fontSize="14px"
                            text="Select a part this message is referring to (Optional)"
                        />
                        <Form.Item
                            className={styles.messageFormItem}
                            name="itemPartName"
                            validateTrigger="onBlur"
                            style={{ width: '50%', marginBottom: '12px' }}
                        >
                            <Select
                                placeholder="Select part name"
                                disabled={loading}
                            >
                                {itemPartNames?.map(name => {
                                    return (
                                        <Select.Option key={name} value={name}>
                                            <Box width="100%">{name}</Box>
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                        <Text
                            fontSize="14px"
                            text="Please choose from the following issues below"
                        />
                        <Form.Item
                            className={styles.messageFormItem}
                            name="subject"
                            rules={[
                                {
                                    required: true,
                                    message: 'Subject is required',
                                },
                            ]}
                            validateTrigger={['onBlur', 'onChange']}
                            style={{ width: '50%', marginBottom: '12px' }}
                        >
                            <Select
                                className={styles.selectBox}
                                placeholder="Select subject"
                                disabled={loading}
                            >
                                {subjectTypes?.map(subject => {
                                    return (
                                        <Select.Option
                                            key={subject.id as string}
                                            value={subject.id as string}
                                        >
                                            <Box width="100%">
                                                {subject.value}
                                            </Box>
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </>
                )}
                <Form.Item
                    className={styles.messageFormItem}
                    name="message"
                    rules={[
                        {
                            validator(rule, value) {
                                if (_.isEmpty((value || '').trim())) {
                                    return Promise.reject('Message is required')
                                } else {
                                    return Promise.resolve()
                                }
                            },
                        },
                    ]}
                    validateTrigger={['onBlur', 'onChange']}
                >
                    <Input.TextArea
                        id="message"
                        name="message"
                        rows={5}
                        placeholder="Type your message here"
                        disabled={loading}
                    />
                </Form.Item>
            </Form>
            <Box marginTop="4px" direction="col">
                {attachments.map(attachment => {
                    return (
                        <Box alignItems="center" key={attachment.id}>
                            <PaperClipOutlined
                                className={styles.messageAttachmentIcon}
                            />
                            <Box
                                marginLeft="5px"
                                marginTop="3px"
                                className={styles.attachmentName}
                            >
                                {attachment.name.length >
                                MAX_FILE_NAME_DISPLAY_LENGTH
                                    ? `${attachment.name.substring(
                                          0,
                                          MAX_FILE_NAME_DISPLAY_LENGTH,
                                      )}...`
                                    : attachment.name}
                            </Box>
                            <Box
                                marginLeft="10px"
                                marginTop="3px"
                                className={styles.attachmentName}
                            >
                                {formatFileSize(attachment.size)}
                            </Box>
                            <CloseOutlined
                                className={styles.removeAttachmentIcon}
                                onClick={() => removeAttachment(attachment.id)}
                                disabled={loading}
                            />
                        </Box>
                    )
                })}
                <Box marginTop="10px" direction="col">
                    {showAttachmentSizeMessage && (
                        <Text
                            fontSize="12px"
                            color="error"
                            text="The total size of file attachments exceeds 25 MB"
                        />
                    )}
                    <Text
                        fontSize="12px"
                        text="The total size of file attachments cannot exceed 25 MB"
                    />
                </Box>
                <Box>
                    <UploadMessageAttachment
                        onSuccess={onUploadComplete}
                        sendButtonDisabled={setSendButtonDisabled}
                    >
                        <Button
                            name="uploadBtn"
                            placement="work-order-message-modal"
                            icon={<UploadOutlined />}
                            className={styles.messageUploadButton}
                            disabled={loading}
                        >
                            Upload Attachment
                        </Button>
                    </UploadMessageAttachment>
                </Box>
            </Box>
        </Modal>
    )
}

export default MessagesModal
